import { Injectable } from "@angular/core";
import { JwtService } from "./jwt.service";
import { IotService } from "./iot.service";
import { HttpClient, HttpParameterCodec } from "@angular/common/http";
import { SessionStateService } from "./session-state.service";
import { Observable } from "rxjs/Observable";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import { environment } from "../../../environments/environment";
import { Router } from "@angular/router";
import { Constants } from "./constants";
import { String } from "aws-sdk/clients/eks";
import { Base64 } from "js-base64";

class CustomEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

const { UserPoolId, ClientId } = environment.aws.cognito;

const userPool = new CognitoUserPool({
  UserPoolId: UserPoolId,
  ClientId: ClientId,
});

@Injectable()
export class AuthenticationService {
  constructor(
    private httpClient: HttpClient,
    private jwtService: JwtService,
    private sessionStateService: SessionStateService,
    private iotService: IotService,
    private router: Router
  ) {}

  login(username: string, password: string): Observable<any> {
    const authenticationData = {
      Username: username.toLocaleLowerCase(),
      Password: password,
    };

    const userData = {
      Username: username.toLocaleLowerCase(),
      Pool: userPool,
    };

    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const cognitoUser = new CognitoUser(userData);

    // cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    return Observable.create((observer) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          observer.next(result);
          observer.complete();
        },
        onFailure: function (err) {
          observer.error(err);
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          observer.error({
            code: Constants.COGNITO_EXCETPION.USER_NOT_CONFIRMED,
          });
        },
      });
    });
  }

  newPassword(
    username: string,
    password: string,
    newPassword: string
  ): Observable<any> {
    this.sessionStateService.clearSession();

    const authenticationData = {
      Username: username.toLocaleLowerCase(),
      Password: password,
    };
    const userData = {
      Username: username.toLocaleLowerCase(),
      Pool: userPool,
    };

    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const cognitoUser = new CognitoUser(userData);

    // cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    return Observable.create((observer) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          observer.next(result);
          observer.complete();
        },
        onFailure: function (err) {
          observer.error(err);
        },
        newPasswordRequired: function (userAttributes, requiredAttributes) {
          delete userAttributes.email_verified;
          cognitoUser.completeNewPasswordChallenge(
            newPassword,
            userAttributes,
            {
              onSuccess: function (result) {
                observer.next(result);
                observer.complete();
              },
              onFailure: function (err) {
                observer.error(err);
              },
            }
          );
        },
      });
    });
  }

  getAuthenticatedUser(): CognitoUser {
    return userPool.getCurrentUser();
  }

  getSession(): Observable<any> {
    return Observable.create((observer) => {
      this.getAuthenticatedUser().getSession((err, session) => {
        if (err) {
          this.logout();
        }
        const token = session.getIdToken().getJwtToken();
        this.jwtService.setTimeToken({
          auth: session.getIdToken().decodePayload().auth_time,
          exp: session.getIdToken().decodePayload().exp,
        });
        this.jwtService.setIdToken(token);
        this.jwtService.setRToken(session.getRefreshToken().getToken());
        this.sessionStateService.setUserId(this.getDecodedUserId());
        observer.next(session);
        observer.complete();
      });
    });
  }

  forgotPassword(username: string): Observable<any> {
    return Observable.create((observer) => {
      this.getCognitoUser(username.toLocaleLowerCase()).forgotPassword({
        onSuccess: function (result) {
          observer.next(result);
          observer.complete();
        },
        onFailure: function (err) {
          observer.error(err);
        },
      });
    });
  }

  confirmPassword(
    username: string,
    verificationCode: string,
    newPassword: string
  ): Observable<any> {
    return Observable.create((observer) => {
      this.getCognitoUser(username.toLocaleLowerCase()).confirmPassword(
        verificationCode,
        newPassword,
        {
          onSuccess: function () {
            observer.next();
            observer.complete();
          },
          onFailure: function (err) {
            observer.error(err);
          },
        }
      );
    });
  }

  logout(): void {
    if (this.getAuthenticatedUser()) {
      this.getAuthenticatedUser().signOut();
    }
    this.iotService.disconnect();
    this.sessionStateService.clearSession();
    this.router.navigate(["/sign-in"]);
  }

  isAuthenticated(): boolean {
    if (userPool.getCurrentUser() == null) {
      return false;
    } else {
      if (this.jwtService.isExpiredToken()) {
        return false;
      } else {
        return true;
      }
    }
    // return userPool.getCurrentUser() != null;
  }

  private getCognitoUser(username: string): CognitoUser {
    const userData = {
      Username: username.toLocaleLowerCase(),
      Pool: userPool,
    };
    return new CognitoUser(userData);
  }

  public getDecodedUserId() {
    let decode;
    const jwt = this.jwtService.getIdToken();
    if (jwt !== undefined) {
      const tokens = jwt.split(".");
      //decode = JSON.parse(window.atob(tokens[1]));
      decode = JSON.parse(Base64.decode(tokens[1]));
    }

    return decode["cognito:username"];
  }

  changePassword(
    username: string,
    oldPassword: string,
    newPassword: string
  ): Observable<any> {
    const userData = {
      Username: username.toLocaleLowerCase(),
      Pool: userPool,
    };
    const authenticationData = {
      Username: username.toLocaleLowerCase(),
      Password: oldPassword,
    };

    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const cognitoUser = new CognitoUser(userData);

    // cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    return Observable.create((observer) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: function (result) {
          cognitoUser.changePassword(
            oldPassword,
            newPassword,
            function (err, changeResult) {
              if (err) {
                alert(err.message || JSON.stringify(err));
                return;
              }
            }
          );
          observer.next(result);
          observer.complete();
        },
        onFailure: function (err) {
          observer.error(err);
        },
      });
    });
  }

  refreshSession(refreshToken, username: String): Observable<any> {
    const userData = {
      Username: username.toLocaleLowerCase(),
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    // cognitoUser.setAuthenticationFlowType("USER_PASSWORD_AUTH");

    const rToken = new CognitoRefreshToken({ RefreshToken: refreshToken });
    return Observable.create((observer) => {
      cognitoUser.refreshSession(rToken, (err, session) => {
        if (err) {
          throw err;
        } else {
          const token = session.getIdToken().getJwtToken();
          this.jwtService.setIdToken(token);
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/core/services/authentication.service.ts